import React, { useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Center,
  Flex,
  Grid,
  Heading,
  Text,
  useColorModeValue,
  keyframes,
  VStack,
  HStack,
  Image,
  Stack,
  Button,
  Link,
  Icon,
  SimpleGrid,
  Spacer,
  Divider,
  Tag,
  Badge,
} from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  Users, 
  Zap, 
  Bot, 
  Activity, 
  Cog, 
  ArrowRight, 
  Compass, 
  Network,
  GitBranch,
  GitMerge,
  GitCommit,
  ArrowUpRight,
  ArrowDownRight,
  Compass as LucideCompass,
  ArrowLeft
} from 'lucide-react';
import {
  MethodistSlide
} from '../../components/base/MethodistSlide';
import NeuralBackground from '../../components/NeuralBackground';
import JIMIWellnessLogo from '../../components/JIMIWellnessLogo';
import useLogoUrl from '../../hooks/useLogoUrl';
import { useAudio } from '../../context/AudioContext';

// Methodist brand colors
const methodistBlue = '#0078C1';
const methodistNavy = '#002F6C';
const methodistTeal = '#0097A7';

// Animation keyframes
const pulseAnimation = keyframes`
  0% { opacity: 0.4; }
  50% { opacity: 0.8; }
  100% { opacity: 0.4; }
`;

const neuralGlowAnimation = keyframes`
  0% { box-shadow: 0 0 5px rgba(0, 114, 206, 0.3); }
  50% { box-shadow: 0 0 15px rgba(0, 114, 206, 0.5); }
  100% { box-shadow: 0 0 5px rgba(0, 114, 206, 0.3); }
`;

const flowAnimation = keyframes`
  0% { transform: translateX(0); opacity: 0.6; }
  50% { opacity: 1; }
  100% { transform: translateX(20px); opacity: 0.6; }
`;

const rotateAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const dashedCircleAnimation = keyframes`
  0% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 100; }
`;

const electricalPulseAnimation = keyframes`
  0% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.1); }
  100% { opacity: 0.3; transform: scale(0.8); }
`;

const neuralSparkAnimation = keyframes`
  0% { opacity: 0; transform: scale(0); }
  50% { opacity: 1; transform: scale(1); }
  100% { opacity: 0; transform: scale(0); }
`;

// Neural wave animation
const neuralWaveAnimation = keyframes`
  0% {
    d: path('M0,50 C30,45 70,55 100,50');
  }
  50% {
    d: path('M0,50 C30,55 70,45 100,50');
  }
  100% {
    d: path('M0,50 C30,45 70,55 100,50');
  }
`;

// Neural data packet animation
const neuralDataPacketAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateX(0%);
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
`;

// Portal Component interface
interface PortalComponentProps {
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  features: string[];
  index: number;
}

// Portal Component
const PortalComponent: React.FC<PortalComponentProps> = ({ title, description, icon: Icon, color, features, index }) => {
  return (
    <Box
      p={5}
      borderWidth="1px"
      borderRadius="lg"
      boxShadow="md"
      bg="white"
      position="relative"
      transition="all 0.3s"
      h="100%"
      animation={`${neuralGlowAnimation} ${3 + index * 0.5}s infinite`}
      _after={{
        content: '""',
        position: 'absolute',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        borderRadius: '8px',
        background: `radial-gradient(circle at center, ${color}10 0%, transparent 70%)`,
        zIndex: '-1',
        opacity: 0,
        transition: 'opacity 0.3s'
      }}
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: 'lg',
        _after: {
          opacity: 1
        }
      }}
    >
      <Flex direction="column" h="100%">
        <Flex align="center" mb={4}>
          <Box
            p={2}
            borderRadius="full"
            bg={`${color}10`}
            color={color}
            mr={3}
          >
            <Icon size={24} />
          </Box>
          <Heading size="md" fontWeight="bold">
            {title}
          </Heading>
        </Flex>
        <Text mb={4} color="gray.600" flex="1">
          {description}
        </Text>
        <Box>
          {features.map((feature, i) => (
            <Flex key={i} align="center" mb={2}>
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg={color}
                mr={2}
                animation={`${pulseAnimation} ${2 + i * 0.5}s infinite`}
              />
              <Text fontSize="sm" color="gray.600">
                {feature}
              </Text>
            </Flex>
          ))}
        </Box>
      </Flex>
    </Box>
  );
};

// Circle Integration interface
interface CircleIntegrationProps {
  portalType: 'clinician' | 'admin';
  color: string;
  title: string;
  icon: React.ElementType;
  mirrored?: boolean;
}

const CircleIntegration: React.FC<CircleIntegrationProps> = ({ portalType, color, title, icon: Icon, mirrored = false }) => {
  const components = portalType === 'clinician' ? [
    { label: 'Personal Resilience', color: methodistBlue },
    { label: 'Culture of Wellness', color: methodistNavy },
    { label: 'Efficiency of Practice', color: methodistBlue }
  ] : [
    { label: 'Wellness AI', color: methodistBlue },
    { label: 'Neural Lab', color: methodistNavy },
    { label: 'Core Mind', color: methodistBlue }
  ];
  
  // Define primary connections to emphasize
  // For SentientIQ: Culture (1) <-> Efficiency (2)
  // For Nexus: Neural Lab (1) <-> Core Mind (2)
  const isPrimaryConnection = (idx: number, targetIdx: number) => {
    if (portalType === 'clinician') {
      return (idx === 1 && targetIdx === 2) || (idx === 2 && targetIdx === 1);
    } else {
      return (idx === 1 && targetIdx === 2) || (idx === 2 && targetIdx === 1);
    }
  };
  
  return (
    <Box
      p={3}
      borderWidth="1px"
      borderRadius="lg"
      boxShadow="md"
      bg="white"
      position="relative"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
      overflow="hidden"
      height="100%"
      animation={`${neuralGlowAnimation} 3s infinite`}
    >
      <Flex direction="column" h="100%">
        <Flex align="center" mb={2}>
          <Box
            p={1.5}
            borderRadius="full"
            bg={`${color}10`}
            color={color}
            mr={2}
          >
            <Icon size={18} />
          </Box>
          <Heading size="sm" fontWeight="bold">
            {title}
          </Heading>
        </Flex>
        <Text fontSize="xs" mb={2} color="gray.600" flex="1">
          {portalType === 'clinician' ? 'Internal connections within SentientIQ' : 'Internal connections within Nexus'}
        </Text>
        
        {/* Neural Network Visualization */}
        <Box 
          position="relative"
          width="100%"
          height="130px"
          borderRadius="md"
          bg={`${color}05`}
          overflow="hidden"
          transform={mirrored ? 'scaleX(-1)' : 'none'}
        >
          {/* Nodes */}
          {components.map((component, idx) => {
            // Calculate positions in a triangle formation
            // For mirrored version, we'll keep the same positions but the scaleX(-1) will flip them
            const positions = [
              { top: '30%', left: '25%' },  // Top left
              { top: '30%', left: '75%' },  // Top right
              { top: '70%', left: '50%' }   // Bottom center
            ];
            
            return (
              <React.Fragment key={idx}>
                {/* Main Node */}
                <Box
                  position="absolute"
                  top={positions[idx].top}
                  left={positions[idx].left}
                  width="12px"
                  height="12px"
                  borderRadius="50%"
                  bg={component.color}
                  boxShadow={`0 0 8px ${component.color}`}
                  animation={`${pulseAnimation} ${2 + idx * 0.3}s infinite`}
                  zIndex="2"
                />
                
                {/* Node Label - we need to counter the mirror effect for text */}
                <Box
                  position="absolute"
                  top={`calc(${positions[idx].top} + 13px)`}
                  left={positions[idx].left}
                  transform={`translateX(-50%) ${mirrored ? 'scaleX(-1)' : ''}`}
                  textAlign="center"
                  width="70px"
                >
                  <Text
                    fontSize="10px"
                    fontWeight="medium"
                    color={`${component.color}`}
                    opacity="0.8"
                  >
                    {idx === 0 ? components[0].label.split(' ')[0] : 
                     idx === 1 ? components[1].label.split(' ')[0] : 
                     idx === 2 ? components[2].label.split(' ')[0] : ''}
                  </Text>
                </Box>
                
                {/* Connections to other nodes (fully connected graph) */}
                {[0, 1, 2].filter(i => i !== idx).map(targetIdx => {
                  // Create lines between all nodes
                  // For mirrored version, we'll adjust the animation direction
                  const flowDirection = mirrored ? (targetIdx < idx) : (targetIdx > idx);
                  const primary = isPrimaryConnection(idx, targetIdx);
                  
                  return (
                    <Box
                      key={`${idx}-${targetIdx}`}
                      position="absolute"
                      top={positions[idx].top}
                      left={positions[idx].left}
                      width={`calc(${Math.abs(parseInt(positions[targetIdx].left as string) - parseInt(positions[idx].left as string))}%)`}
                      height={primary ? "2px" : "1px"}
                      bg={primary ? `${component.color}90` : `${component.color}40`}
                      transform={`rotate(${targetIdx === 2 ? 30 : targetIdx === 0 ? -30 : 0}deg)`}
                      transformOrigin={targetIdx > idx ? 'left center' : 'right center'}
                      zIndex="1"
                      opacity={primary ? 1 : 0.5}
                      boxShadow={primary ? `0 0 3px ${component.color}` : "none"}
                    >
                      {/* Animated pulse along the connection */}
                      <Box
                        position="absolute"
                        top={primary ? "-3px" : "-2px"}
                        left={flowDirection ? '10%' : 'auto'}
                        right={!flowDirection ? '10%' : 'auto'}
                        width={primary ? "6px" : "4px"}
                        height={primary ? "6px" : "4px"}
                        borderRadius="50%"
                        bg="white"
                        boxShadow={`0 0 ${primary ? 6 : 3}px ${component.color}`}
                        animation={`${flowAnimation} ${primary ? 1.2 : 1.8}s infinite ${primary ? 0 : (idx + targetIdx) * 0.2}s`}
                        zIndex="3"
                      />
                      
                      {/* Add a second pulse for primary connections */}
                      {primary && (
                        <Box
                          position="absolute"
                          top="-3px"
                          left={!flowDirection ? '60%' : 'auto'}
                          right={flowDirection ? '60%' : 'auto'}
                          width="6px"
                          height="6px"
                          borderRadius="50%"
                          bg="white"
                          boxShadow={`0 0 6px ${component.color}`}
                          animation={`${flowAnimation} 1.2s infinite 0.6s`}
                          zIndex="3"
                        />
                      )}
                    </Box>
                  );
                })}
              </React.Fragment>
            );
          })}
          
          {/* Neural Sparks - random neural activity */}
          {[...Array(5)].map((_, idx) => {
            const randomTop = 20 + Math.random() * 60; // 20-80%
            const randomLeft = 20 + Math.random() * 60; // 20-80%
            const randomDelay = Math.random() * 2;
            const randomDuration = 1 + Math.random() * 1.5;
            
            return (
              <Box
                key={`neural-spark-${idx}`}
                position="absolute"
                top={`${randomTop}%`}
                left={`${randomLeft}%`}
                width="3px"
                height="3px"
                borderRadius="50%"
                bg="white"
                boxShadow={`0 0 3px ${methodistBlue}`}
                animation={`${neuralSparkAnimation} ${randomDuration}s infinite ${randomDelay}s`}
                zIndex="1"
                opacity="0.7"
              />
            );
          })}
          
          {/* Central Hub - pulsing center */}
          <Box
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="16px"
            height="16px"
            borderRadius="50%"
            bg={`${methodistTeal}30`}
            boxShadow={`0 0 10px ${methodistTeal}`}
            animation={`${pulseAnimation} 3s infinite`}
            zIndex="1"
          />
        </Box>
      </Flex>
    </Box>
  );
};

const UnifiedPortalBridge: React.FC = () => {
  return (
    <Box
      p={3}
      borderWidth="1px"
      borderRadius="lg"
      boxShadow="md"
      bg="rgba(255, 255, 255, 0.9)"
      position="relative"
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
      overflow="hidden"
      height="120px"
      width="95%"
      mx="auto"
      _before={{
        content: '""',
        position: 'absolute',
        top: '-20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        background: `radial-gradient(circle, ${methodistBlue}30 0%, transparent 70%)`,
        zIndex: '1',
        animation: `${pulseAnimation} 3s infinite`
      }}
    >
      <Flex direction="column" h="100%" position="relative" zIndex="2">
        <Flex align="center" mb={1}>
          <Box
            p={1.5}
            borderRadius="full"
            bg={`${methodistBlue}20`}
            color={methodistBlue}
            mr={2}
            boxShadow={`0 0 10px ${methodistBlue}40`}
          >
            <Network size={16} />
          </Box>
          <Heading size="xs" fontWeight="bold" color={methodistNavy}>
            Neural Integration Bridge
          </Heading>
        </Flex>
        <Text fontSize="xs" color="gray.600" mb={2}>
          Bidirectional neural pathways connecting cortices
        </Text>
        
        {/* Neural Wave Visualization */}
        <Box position="relative" height="50px" width="100%">
          {/* Base Wave Path */}
          <Box as="svg" width="100%" height="50px" viewBox="0 0 100 100" preserveAspectRatio="none" position="absolute" top="0" left="0">
            <Box
              as="path"
              d="M0,50 C30,40 70,60 100,50"
              fill="none"
              stroke={methodistBlue}
              strokeWidth="2"
              strokeDasharray="3,2"
              opacity="0.8"
              animation={`${neuralWaveAnimation} 2.5s ease-in-out infinite`}
            />
            <Box
              as="path"
              d="M0,50 C30,60 70,40 100,50"
              fill="none"
              stroke={methodistTeal}
              strokeWidth="2"
              strokeDasharray="3,2"
              opacity="0.8"
              animation={`${neuralWaveAnimation} 2.5s ease-in-out infinite reverse`}
            />
          </Box>
          
          {/* Neural Data Packets */}
          {[...Array(4)].map((_, i) => (
            <React.Fragment key={`data-packet-${i}`}>
              {/* Left to Right Data Packet */}
              <Box
                position="absolute"
                top="calc(50% - 3px)"
                left="0"
                width="6px"
                height="6px"
                borderRadius="full"
                bg={methodistBlue}
                boxShadow={`0 0 8px ${methodistBlue}`}
                opacity="0"
                animation={`${neuralDataPacketAnimation} ${2.5 + i * 0.5}s linear infinite ${i * 0.8}s`}
                zIndex="3"
              />
              
              {/* Right to Left Data Packet */}
              <Box
                position="absolute"
                top="calc(50% - 3px)"
                left="100%"
                width="6px"
                height="6px"
                borderRadius="full"
                bg={methodistTeal}
                boxShadow={`0 0 8px ${methodistTeal}`}
                opacity="0"
                animation={`${neuralDataPacketAnimation} ${2.5 + i * 0.5}s linear infinite ${i * 0.8}s`}
                style={{ animationDirection: 'reverse' }}
                zIndex="3"
              />
            </React.Fragment>
          ))}
          
          {/* Neural Nodes */}
          <Box
            position="absolute"
            left="0"
            top="calc(50% - 5px)"
            width="10px"
            height="10px"
            borderRadius="full"
            bg={methodistBlue}
            boxShadow={`0 0 10px ${methodistBlue}`}
            zIndex="4"
          />
          <Box
            position="absolute"
            right="0"
            top="calc(50% - 5px)"
            width="10px"
            height="10px"
            borderRadius="full"
            bg={methodistTeal}
            boxShadow={`0 0 10px ${methodistTeal}`}
            zIndex="4"
          />
        </Box>
      </Flex>
    </Box>
  );
};

const ThreeDimensionsSlide: React.FC = () => {
  const { logoUrl } = useLogoUrl();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  
  // Use the audio context
  const { playAudioForSlide } = useAudio();

  useEffect(() => {
    // Play audio when component mounts with a slight delay to ensure DOM is ready
    const audioTimer = setTimeout(() => {
      console.log('Starting audio playback for three-dimensions slide...');
      playAudioForSlide('three-dimensions');
    }, 800);
    
    // Cleanup function
    return () => {
      clearTimeout(audioTimer);
      // Audio cleanup is handled by AudioContext
    };
  }, [playAudioForSlide]);

  // Methodist brand colors
  const methodistBlue = '#0078C1';
  const methodistNavy = '#002F6C';
  const methodistTeal = '#0097A7';

  // Animation keyframes
  const pulseAnimation = keyframes`
    0% { opacity: 0.4; }
    50% { opacity: 0.8; }
    100% { opacity: 0.4; }
  `;

  const neuralGlowAnimation = keyframes`
    0% { box-shadow: 0 0 5px rgba(0, 114, 206, 0.3); }
    50% { box-shadow: 0 0 15px rgba(0, 114, 206, 0.5); }
    100% { box-shadow: 0 0 5px rgba(0, 114, 206, 0.3); }
  `;

  const flowAnimation = keyframes`
    0% { transform: translateX(0); opacity: 0.6; }
    50% { opacity: 1; }
    100% { transform: translateX(20px); opacity: 0.6; }
  `;

  const rotateAnimation = keyframes`
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  `;

  const dashedCircleAnimation = keyframes`
    0% { stroke-dashoffset: 0; }
    100% { stroke-dashoffset: 100; }
  `;

  const electricalPulseAnimation = keyframes`
    0% { opacity: 0.3; transform: scale(0.8); }
    50% { opacity: 1; transform: scale(1.1); }
    100% { opacity: 0.3; transform: scale(0.8); }
  `;

  const neuralSparkAnimation = keyframes`
    0% { opacity: 0; transform: scale(0); }
    50% { opacity: 1; transform: scale(1); }
    100% { opacity: 0; transform: scale(0); }
  `;

  // Clinician Portal components
  const clinicianPortalComponents = [
    {
      title: "Personal Resilience",
      description: "Individual wellness practices and AI-powered coaching for clinician well-being",
      icon: Brain,
      color: methodistBlue,
      features: [
        "AI-powered adaptability coaching",
        "Personalized wellness recommendations",
        "Real-time emotional support"
      ],
      index: 0
    },
    {
      title: "Culture of Wellness",
      description: "Supportive community environment that fosters connection and belonging",
      icon: Users,
      color: methodistNavy,
      features: [
        "Community engagement tools",
        "Peer support networks",
        "Team wellness challenges"
      ],
      index: 1
    },
    {
      title: "Efficiency of Practice",
      description: "Optimized workflows that reduce administrative burden and reclaim joy in practice",
      icon: Zap,
      color: methodistBlue,
      features: [
        "Workflow optimization tools",
        "AI-assisted documentation",
        "Practice analytics"
      ],
      index: 2
    }
  ];

  // Admin Portal components
  const adminPortalComponents = [
    {
      title: "Wellness AI",
      description: "Content management and personalized recommendations for clinician well-being",
      icon: Bot,
      color: methodistBlue,
      features: [
        "Content curation and management",
        "Personalized recommendation engine",
        "Adaptive learning algorithms"
      ],
      index: 0
    },
    {
      title: "Neural Lab",
      description: "Experimental hub for AI and analytics development in healthcare contexts",
      icon: Activity,
      color: methodistNavy,
      features: [
        "LLM experimentation and fine-tuning",
        "Advanced analytics on wellness metrics",
        "Community insight generation"
      ],
      index: 1
    },
    {
      title: "Core Mind",
      description: "Central orchestration system for platform management and governance",
      icon: Cog,
      color: methodistBlue,
      features: [
        "Platform-wide orchestration",
        "Security and compliance management",
        "Workflow optimization engine"
      ],
      index: 2
    }
  ];

  // Portal connections
  const portalConnections = [
    {
      source: "Personal Resilience",
      target: "Wellness AI",
      description: "Personal well-being tools powered by AI content curation",
      color: methodistBlue,
      index: 0
    },
    {
      source: "Culture of Wellness",
      target: "Neural Lab",
      description: "Community insights analyzed through advanced data science",
      color: methodistNavy,
      index: 1
    },
    {
      source: "Efficiency of Practice",
      target: "Core Mind",
      description: "Workflow optimization orchestrated by intelligent systems",
      color: methodistBlue,
      index: 2
    }
  ];

  // Internal connections within SentientIQ
  const sentientIQConnections = [
    {
      source: "Personal Resilience",
      target: "Culture of Wellness",
      description: "Individual well-being enhances community engagement",
      color: methodistBlue,
      index: 0
    },
    {
      source: "Culture of Wellness",
      target: "Efficiency of Practice",
      description: "Supportive culture improves workflow effectiveness",
      color: methodistNavy,
      index: 1
    },
    {
      source: "Efficiency of Practice",
      target: "Personal Resilience",
      description: "Optimized workflows reduce burnout and increase resilience",
      color: methodistBlue,
      index: 2
    }
  ];

  // Internal connections within Nexus
  const nexusConnections = [
    {
      source: "Wellness AI",
      target: "Neural Lab",
      description: "Content curation informed by data science insights",
      color: methodistBlue,
      index: 0
    },
    {
      source: "Neural Lab",
      target: "Core Mind",
      description: "Analytics drive platform orchestration decisions",
      color: methodistNavy,
      index: 1
    },
    {
      source: "Core Mind",
      target: "Wellness AI",
      description: "System governance enhances content personalization",
      color: methodistBlue,
      index: 2
    }
  ];

  return (
    <MethodistSlide showLogo={false}>
      <Center w="100%" h="100%">
        <Box position="relative" height="100%" width="100%" maxW="1400px" overflow="hidden">
          <Box position="absolute" top={0} right={0} bottom={0} left={0} zIndex={-1} opacity={0.05}>
            <NeuralBackground />
          </Box>

          <Flex direction="column" height="100%" p={{ base: 3, md: 5 }}>
            {/* Header */}
            <Flex align="center" mb={{ base: 4, md: 5 }} justify="space-between">
              {logoUrl && (
                <img src={logoUrl} alt="Houston Methodist" style={{ height: "40px" }} />
              )}
              <JIMIWellnessLogo size="md" showTagline={true} />
            </Flex>

            {/* Title and Description */}
            <Box mb={6} textAlign="center">
              <Heading 
                as="h2"
                size="lg"  
                color={methodistBlue}
                textAlign="center"
                mb={8}
                mt={4}
                fontSize="24px"  
              >
                Integrated Digital Platform Architecture
              </Heading>
              <Text fontSize="md" color="gray.600" maxW="800px" mx="auto" display="none">
                A revolutionary approach that mirrors neural pathways, creating three interconnected dimensions that work together to support clinician well-being
              </Text>
            </Box>

            {/* Main content */}
            <Grid
              templateColumns={{ base: "1fr", lg: "1.2fr 0.6fr 1.2fr" }}
              templateRows={{ base: "auto", lg: "auto" }}
              gap={{ base: 6, lg: 0 }}
              flex="1"
              overflow="hidden"
              width="100%"
              position="relative"
              minHeight="650px"
            >
              {/* Left Cortex */}
              <Box 
                width="100%" 
                position="relative"
                transform={{ base: "none", lg: "perspective(1000px) rotateY(10deg)" }}
                transformOrigin="right center"
                borderRadius="full"
                borderRightRadius="0"
                overflow="visible"
                pl={{ base: 0, lg: 4 }}
                pr={{ base: 0, lg: 2 }}
                display="flex"
                flexDirection="column"
                zIndex="1"
              >
                <VStack spacing={1} position="relative" right={{ base: 0, lg: "-45%" }} mb={2}>
                  <Heading 
                    size="sm"
                    mb={0.5} 
                    textAlign="center" 
                    color={methodistBlue}
                    textShadow={`0 0 10px ${methodistBlue}30`}
                  >
                    SentientIQ
                  </Heading>
                  <Text
                    fontSize="xs"
                    color={`${methodistNavy}90`}
                    textTransform="uppercase"
                    letterSpacing="wider"
                    textAlign="center"
                    mb={1}
                  >
                    Clinician Portal
                  </Text>
                </VStack>

                {/* Circle visualization for internal connections - positioned for oval */}
                <Box 
                  width="100%" 
                  display="flex" 
                  justifyContent="flex-end"
                  pr={{ base: 0, lg: 2 }}
                  mb={3}
                  position="relative"
                  right={{ base: 0, lg: "-30%" }}  
                  zIndex="2"
                >
                  <Box width={{ base: "100%", lg: "75%" }}>
                    <CircleIntegration 
                      portalType="clinician"
                      color={methodistBlue}
                      title="Internal Integration"
                      icon={GitBranch}
                      mirrored={false}
                    />
                  </Box>
                </Box>

                <VStack 
                  spacing={4} 
                  width="100%"
                  borderRadius="full"
                  borderRightRadius="0"
                  padding={3}
                  position="relative"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: "-10%",
                    bottom: "-10%",
                    left: "-50%",
                    width: "100%",
                    borderRadius: "full",
                    borderRightRadius: "0",
                    border: "2px dashed",
                    borderRight: "none",
                    borderColor: `${methodistBlue}30`,
                    zIndex: -1,
                    display: { base: "none", lg: "block" }
                  }}
                >
                  {clinicianPortalComponents.map((component, idx) => (
                    <Box 
                      key={component.index}
                      width="100%"
                      display="flex"
                      justifyContent="flex-end"
                      position="relative"
                      right={{ 
                        base: 0, 
                        lg: idx === 0 ? "-30%" : 
                           idx === 1 ? "-15%" : 
                           idx === 2 ? "-30%" : 0 
                      }}
                      transition="all 0.3s ease"
                    >
                      <Box width={{ base: "100%", lg: "80%" }}>
                        <PortalComponent
                          title={component.title}
                          description={component.description}
                          icon={component.icon}
                          color={component.color}
                          features={component.features}
                          index={component.index}
                        />
                      </Box>
                    </Box>
                  ))}
                </VStack>
              </Box>

              {/* Center */}
              <Box position="relative">
                {/* Neural Integration - Positioned between Culture of Wellness and Neural Lab */}
                <Box
                  position="absolute"
                  top="65%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  width="116%"
                  zIndex="10"
                  css={{
                    "& svg": {
                      filter: `drop-shadow(0 0 3px ${methodistBlue}40)`
                    }
                  }}
                >
                  {/* Unified bridge visualization for portal connection */}
                  <Box
                    width="100%"
                    transform="scale(0.6)"
                    mb={4}
                    position="relative"
                    zIndex="2"
                  >
                    <UnifiedPortalBridge />
                  </Box>
                </Box>

                {/* Brain Connection Line */}
                <Box
                  position="absolute"
                  top="65%"
                  left="-7.5%"
                  right="-7.5%"
                  height="2px"
                  bg={`linear-gradient(90deg, ${methodistBlue}20, ${methodistBlue}60, ${methodistBlue}20)`}
                  zIndex="-1"
                  display={{ base: "none", lg: "block" }}
                  boxShadow={`0 0 10px ${methodistBlue}40`}
                  css={{
                    animation: `${pulseAnimation} 3s infinite`,
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "2px",
                      borderRadius: "full",
                      background: `radial-gradient(circle, ${methodistBlue}40 0%, transparent 70%)`,
                      animation: `${pulseAnimation} 1s infinite`
                    }
                  }}
                />
              </Box>

              {/* Right Cortex */}
              <Box 
                width="100%"
                position="relative"
                transform={{ base: "none", lg: "perspective(1000px) rotateY(-10deg)" }}
                transformOrigin="left center"
                borderRadius="full"
                borderLeftRadius="0"
                overflow="visible"
                pl={{ base: 0, lg: 2 }}
                pr={{ base: 0, lg: 4 }}
                display="flex"
                flexDirection="column"
                zIndex="1"
              >
                <VStack spacing={1} position="relative" left={{ base: 0, lg: "-45%" }} mb={2}>
                  <Heading 
                    size="sm"
                    mb={0.5} 
                    textAlign="center" 
                    color={methodistBlue}
                    textShadow={`0 0 10px ${methodistBlue}30`}
                  >
                    Nexus
                  </Heading>
                  <Text
                    fontSize="xs"
                    color={`${methodistNavy}90`}
                    textTransform="uppercase"
                    letterSpacing="wider"
                    textAlign="center"
                    mb={1}
                  >
                    Admin Portal
                  </Text>
                </VStack>
                
                {/* Circle visualization for internal connections - positioned for oval */}
                <Box 
                  width="100%" 
                  display="flex" 
                  justifyContent="flex-start"
                  pl={{ base: 0, lg: 2 }}
                  mb={3}
                  position="relative"
                  left={{ base: 0, lg: "-30%" }}  
                  zIndex="2"
                >
                  <Box width={{ base: "100%", lg: "75%" }}>
                    <CircleIntegration 
                      portalType="admin"
                      color={methodistBlue}
                      title="Internal Integration"
                      icon={GitMerge}
                      mirrored={true}
                    />
                  </Box>
                </Box>
                
                <VStack 
                  spacing={4} 
                  width="100%"
                  borderRadius="full"
                  borderLeftRadius="0"
                  padding={3}
                  position="relative"
                  _before={{
                    content: '""',
                    position: "absolute",
                    top: "-10%",
                    bottom: "-10%",
                    right: "-50%",
                    width: "100%",
                    borderRadius: "full",
                    borderLeftRadius: "0",
                    border: "2px dashed",
                    borderLeft: "none",
                    borderColor: `${methodistBlue}30`,
                    zIndex: -1,
                    display: { base: "none", lg: "block" }
                  }}
                >
                  {adminPortalComponents.map((component, idx) => (
                    <Box 
                      key={component.index}
                      width="100%"
                      display="flex"
                      justifyContent="flex-start"
                      position="relative"
                      left={{ 
                        base: 0, 
                        lg: idx === 0 ? "-30%" : 
                           idx === 1 ? "-15%" : 
                           idx === 2 ? "-30%" : 0 
                      }}
                      transition="all 0.3s ease"
                    >
                      <Box width={{ base: "100%", lg: "80%" }}>
                        <PortalComponent
                          title={component.title}
                          description={component.description}
                          icon={component.icon}
                          color={component.color}
                          features={component.features}
                          index={component.index}
                        />
                      </Box>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Grid>
            
            {/* Footer - Comprehensive Approach */}
            <Box 
              mt={6} 
              p={4}
              bg={`rgba(255, 255, 255, 0.8)`}
              borderRadius="md" 
              borderWidth="1px" 
              borderColor={`${methodistBlue}30`}
              boxShadow={`0 0 15px ${methodistBlue}30`}
              maxW="600px"
              mx="auto"
              transform="scale(0.85)"
              transformOrigin="top center"
              position="relative"
              overflow="hidden"
              _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "2px",
                background: `linear-gradient(90deg, transparent, ${methodistBlue}, transparent)`,
                animation: `${neuralGlowAnimation} 3s infinite`
              }}
            >
              <HStack mb={2} spacing={3} position="relative">
                <Flex 
                  bg={`${methodistBlue}`} 
                  p={2}
                  borderRadius="md" 
                  color="white"
                  boxShadow={`0 0 8px ${methodistBlue}`}
                  animation={`${pulseAnimation} 3s infinite`}
                >
                  <Network size={16} />
                </Flex>
                <Text 
                  fontWeight="bold" 
                  fontSize="sm" 
                  color={methodistNavy}
                  textShadow={`0 0 5px ${methodistBlue}20`}
                >
                  Comprehensive Approach to Clinician Well-being
                </Text>
              </HStack>
              <Text fontSize="xs" lineHeight="1.6" position="relative" zIndex="1">
                The JIMI Wellness Platform integrates clinician-facing tools with powerful administrative capabilities, 
                creating a self-reinforcing <Box as="span" color={methodistBlue} fontWeight="medium">neural network of well-being</Box> that addresses burnout at its root causes rather than just treating symptoms. 
                This architecture ensures that as clinicians engage with one dimension, they simultaneously strengthen the others, 
                transforming the entire experience of clinical practice through <Box as="span" color={methodistBlue} fontWeight="medium">interconnected neural pathways</Box>.
              </Text>
              
              {/* Neural Sparks */}
              {[...Array(3)].map((_, idx) => {
                const randomTop = 20 + Math.random() * 60; // 20-80%
                const randomLeft = 20 + Math.random() * 60; // 20-80%
                const randomDelay = Math.random() * 2;
                
                return (
                  <Box
                    key={`footer-spark-${idx}`}
                    position="absolute"
                    top={`${randomTop}%`}
                    left={`${randomLeft}%`}
                    width="3px"
                    height="3px"
                    borderRadius="50%"
                    bg="white"
                    boxShadow={`0 0 3px ${methodistBlue}`}
                    animation={`${neuralSparkAnimation} 2s infinite ${randomDelay}s`}
                    zIndex="0"
                    opacity="0.7"
                  />
                );
              })}
            </Box>
          </Flex>
        </Box>
      </Center>
    </MethodistSlide>
  );
};

export default ThreeDimensionsSlide;
